import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import MultiSelectionDropdown from '@components/tailwind-components/MultiSelectionDropdownT';
import type { LLMBlockSelectionDTO } from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import { useLLMBlockSelector } from '@hooks/flow/block/useLLMBlockSelector';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Props {
  onConfirm: (
    autoTraining: boolean,
    autoInclude: boolean,
    selectedLLMBlocks?: LLMBlockSelectionDTO[],
  ) => void;
  showAutoInclude?: boolean;
  sharedWithCompanyIds?: string[];
}

export const PostProcessConfigurationModal: React.FC<Props> = ({
  onConfirm,
  showAutoInclude = true,
  sharedWithCompanyIds = [],
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const [autoTraining, setAutoTraining] = useState(false);
  const [autoInclude, setAutoInclude] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);

  const { allBlocks, blockTranslations } = useLLMBlockSelector({
    enabled: autoInclude,
    sharedWithCompanyIds,
  });

  const handleAutoTrainingChange = (checked: boolean) => {
    setAutoTraining(checked);
    if (showAutoInclude) {
      setAutoInclude(false);
      setSelectedBlockIds([]);
    }
  };

  const handleAutoIncludeChange = (checked: boolean) => {
    setAutoInclude(checked);
    if (!checked) {
      setSelectedBlockIds([]);
    }
  };

  const handleToggleBlock = (blockId: string) => {
    setSelectedBlockIds((prev) =>
      prev.includes(blockId)
        ? prev.filter((id) => id !== blockId)
        : [...prev, blockId],
    );
  };

  const isConfirmDisabled = autoInclude && selectedBlockIds.length === 0;

  const handleConfirm = () => {
    hideModal();
    const selectedLLMBlocks = autoInclude
      ? selectedBlockIds.map((blockId) => ({
          blockId,
          companyId:
            allBlocks.find(
              (availableBlock) => availableBlock.blockId === blockId,
            )?.companyId ?? '',
        }))
      : undefined;
    onConfirm(autoTraining, autoInclude, selectedLLMBlocks);
  };

  return (
    <form className='tailwind-modal-form flex-1 min-h-[200px] px-2'>
      <div className='tailwind-modal-body flex flex-col h-inherit border border-primary-base rounded-md bg-gray-100 px-0 py-0'>
        <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
          {t('data-sources.post-process-configuration.subtitle')}
        </div>
        <div className='flex flex-col text-primary-base px-3 py-3 gap-3'>
          <div className='flex flex-col text-primary-base'>
            <div className='flex items-center gap-2'>
              <MaterialIcon icon='info' />
              <span className='text-tiny italic text-gray-400'>
                {t('data-sources.post-process-configuration.info')}
              </span>
            </div>
            <div className='flex items-center gap-2 mt-4 ml-3 text-primary-base'>
              <CheckboxTogglerT
                toggleSize='base'
                label={t(
                  'data-sources.post-process-configuration.auto-training',
                )}
                field='autoTraining'
                colorScheme='default'
                labelClassName='text-primary-base text-sm font-medium'
                id='auto-training'
                checked={autoTraining}
                onChange={(event) =>
                  handleAutoTrainingChange(event.target.checked)
                }
              />
            </div>
          </div>

          {showAutoInclude && (
            <div
              className={`flex flex-col text-primary-base border-t border-gray-200 pt-2 ${!autoTraining ? 'opacity-40' : ''}`}>
              <div className='flex items-center gap-2'>
                <MaterialIcon icon='info' />
                <span className='text-tiny italic text-gray-400'>
                  {t(
                    'data-sources.post-process-configuration.auto-include-info',
                  )}
                </span>
              </div>
              <div className='flex items-center gap-2 mt-4 ml-3 text-primary-base'>
                <CheckboxTogglerT
                  toggleSize='base'
                  label={t(
                    'data-sources.post-process-configuration.auto-include',
                  )}
                  field='autoInclude'
                  colorScheme='default'
                  labelClassName={`text-sm font-medium ${!autoTraining ? 'text-gray-400' : 'text-primary-base'}`}
                  id='auto-include'
                  checked={autoInclude}
                  onChange={(event) =>
                    handleAutoIncludeChange(event.target.checked)
                  }
                  options={{
                    disabled: !autoTraining,
                  }}
                />
              </div>
              {autoInclude && (
                <>
                  <div className='flex items-center gap-2 mt-3'>
                    <MaterialIcon icon='info' />
                    <span className='text-tiny italic text-gray-400'>
                      {t('data-sources.manual-training.auto-include-info')}
                    </span>
                  </div>
                  <div className='flex flex-col gap-1 mt-3 ml-3'>
                    <span className='text-xs font-medium text-primary-base'>
                      {t(
                        'data-sources.post-process-configuration.select-llm-blocks',
                      )}
                    </span>
                    <MultiSelectionDropdown
                      selectedValues={selectedBlockIds}
                      availableValues={allBlocks.map((block) => block.blockId)}
                      valueTranslations={blockTranslations}
                      placeholder={t(
                        'data-sources.post-process-configuration.select-llm-blocks-placeholder',
                      )}
                      selectValue={handleToggleBlock}
                      keepOpen={true}
                      maxVisibleBadges={4}
                      autoSizeBadges={true}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className='tailwind-modal-footer px-0'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          disabled={isConfirmDisabled}
          onClick={handleConfirm}>
          {t('general.buttons.continue')}
        </ButtonT>
      </div>
    </form>
  );
};

export default PostProcessConfigurationModal;
